import { scheduleDateAtom } from '@/atoms/sessionAtoms';
import { ehrFuseSettings } from '@/components/layout/Navigation/sessions/constants';
import { useDebouncedSessionSearchValue } from '@/components/layout/Navigation/sessions/hooks/useDebouncedSessionSearchValue';
import { useIsMultiProviderScheduleEnabled } from '@/features/flags';
import { activeFilterProviderAtom } from '@/features/sessions/components/appointment-list/atoms';
import { useScheduleFilterOptions } from '@/features/sessions/components/appointment-list/hooks/useScheduleFilterOptions';
import {
  useClearIntegrationFilters,
  useScheduleIntegrationFilters,
} from '@/features/sessions/components/appointment-list/utils/integrationFilterTags';
import { useQueryIntegrationsMixinGetSchedule } from '@/hooks/integrations/integrations-framework/mixins/useQueryIntegrationsMixinGetSchedule';
import {
  IntegrationLaunchSource,
  useIntegrationEhrInfo,
} from '@/hooks/integrations/useIntegrationEhrInfo';
import { useQueryEHRAppointments } from '@/hooks/query/integrations/useQueryEHRAppointments';
import { scheduleHasDisplayFacets } from '@heidi/session';

import { formatToDateOnly } from '@/utils/dates';
import { isToday } from 'date-fns';
import Fuse from 'fuse.js';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';

export { isEHRAppointment } from '../utils/isEHRAppointment';

export const useRetrieveAppointmentOrSchedule = () => {
  const [scheduleDate, setScheduleDate] = useAtom(scheduleDateAtom);
  const sessionListSearchValue = useDebouncedSessionSearchValue();
  const clearIntegrationFilters = useClearIntegrationFilters();
  const prevScheduleDateRef = useRef(scheduleDate);
  const isInitialScheduleDateRef = useRef(true);

  useEffect(() => {
    if (isInitialScheduleDateRef.current) {
      isInitialScheduleDateRef.current = false;
      prevScheduleDateRef.current = scheduleDate;
      return;
    }

    const previousDate = formatToDateOnly(prevScheduleDateRef.current);
    const nextDate = formatToDateOnly(scheduleDate);
    if (previousDate !== nextDate) {
      clearIntegrationFilters();
    }
    prevScheduleDateRef.current = scheduleDate;
  }, [scheduleDate, clearIntegrationFilters]);

  const {
    integrationLaunchSource,
    integrationEhrProvider,
    isIntegrationOnboardingSession,
    isPending: isIntegrationEhrInfoPending,
    isError: isIntegrationEhrInfoError,
  } = useIntegrationEhrInfo();

  const [activeFilterProvider, setActiveFilterProvider] = useAtom(
    activeFilterProviderAtom
  );

  useEffect(() => {
    if (isIntegrationEhrInfoPending) return;

    if (integrationEhrProvider !== activeFilterProvider) {
      clearIntegrationFilters();
      setActiveFilterProvider(integrationEhrProvider);
    }
  }, [
    activeFilterProvider,
    clearIntegrationFilters,
    integrationEhrProvider,
    isIntegrationEhrInfoPending,
    setActiveFilterProvider,
  ]);

  const {
    data: ehrAppts,
    isFetching: isEHRApptsPending,
    isError: isEHRApptsError,
    refetch: refetchEHRAppts,
  } = useQueryEHRAppointments(scheduleDate);
  const scheduleDateInput = formatToDateOnly(scheduleDate) ?? undefined;

  const isMultiProviderScheduleEnabled = useIsMultiProviderScheduleEnabled();
  const scheduleIntegrationFilters = useScheduleIntegrationFilters(
    isMultiProviderScheduleEnabled
  );

  const {
    data: integrationsScheduleData,
    isPending: isIntegrationsSchedulePending,
    isError: isIntegrationsScheduleError,
    refetch: refetchIntegrationsSchedule,
  } = useQueryIntegrationsMixinGetSchedule({
    start_date: scheduleDateInput,
    end_date: scheduleDateInput,
    filter: scheduleIntegrationFilters,
  });

  /** Any provider whose schedule rows carry display_facets enables the options query — not just Athena. */
  const hasScheduleFilterFacets = scheduleHasDisplayFacets(
    integrationsScheduleData ?? []
  );

  const {
    data: scheduleFilterOptions,
    isPending: isScheduleFilterOptionsPending,
    refetch: refetchScheduleFilterOptions,
  } = useScheduleFilterOptions({
    scheduleDate: scheduleDateInput,
    enabled: isIntegrationOnboardingSession && hasScheduleFilterFacets,
  });

  const integrationsScheduleFuse = useMemo(
    () => new Fuse(integrationsScheduleData || [], ehrFuseSettings),
    [integrationsScheduleData]
  );

  const ehApptsFuse = useMemo(
    () => new Fuse(ehrAppts?.appointments || [], ehrFuseSettings),
    [ehrAppts?.appointments]
  );

  const searchResult = useMemo(() => {
    if (isIntegrationOnboardingSession) {
      return sessionListSearchValue
        ? integrationsScheduleFuse
            .search(sessionListSearchValue)
            .map((result) => result.item)
        : integrationsScheduleData;
    } else if (integrationLaunchSource === IntegrationLaunchSource.LEGACY_EHR_PROFILE) {
      return sessionListSearchValue
        ? ehApptsFuse.search(sessionListSearchValue).map((result) => result.item)
        : ehrAppts?.appointments;
    }
    return [];
  }, [
    isIntegrationOnboardingSession,
    integrationLaunchSource,
    sessionListSearchValue,
    integrationsScheduleFuse,
    integrationsScheduleData,
    ehApptsFuse,
    ehrAppts?.appointments,
  ]);

  // preselect date if have ehr profile but schedule date is empty
  useEffect(() => {
    if (
      integrationLaunchSource === IntegrationLaunchSource.NONE ||
      (scheduleDate && isToday(scheduleDate))
    ) {
      return;
    }
    if (scheduleDate === undefined) {
      setScheduleDate(new Date());
    }
  }, [scheduleDate, setScheduleDate, integrationLaunchSource]);

  const isSchedulePending =
    isIntegrationEhrInfoPending ||
    (isIntegrationOnboardingSession ? isIntegrationsSchedulePending : isEHRApptsPending);

  const isOptionsPending =
    isIntegrationOnboardingSession &&
    Boolean(scheduleIntegrationFilters) &&
    isScheduleFilterOptionsPending;

  /** List loading — display query only; does not wait on filter-dropdown options fetch. */
  const isPending = isSchedulePending;

  const isError =
    isIntegrationEhrInfoError ||
    (isIntegrationOnboardingSession ? isIntegrationsScheduleError : isEHRApptsError);

  const refetch = useCallback(() => {
    if (isIntegrationOnboardingSession) {
      void refetchIntegrationsSchedule();
      if (scheduleIntegrationFilters) {
        void refetchScheduleFilterOptions();
      }
    } else {
      void refetchEHRAppts();
    }
  }, [
    isIntegrationOnboardingSession,
    refetchIntegrationsSchedule,
    refetchScheduleFilterOptions,
    refetchEHRAppts,
    scheduleIntegrationFilters,
  ]);

  return useMemo(
    () => ({
      isPending,
      isSchedulePending,
      isOptionsPending,
      isError,
      searchResult,
      scheduleFilterOptions,
      refetch,
    }),
    [
      isPending,
      isSchedulePending,
      isOptionsPending,
      isError,
      searchResult,
      scheduleFilterOptions,
      refetch,
    ]
  );
};
